import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatDateIt } from "@/lib/format";
import { Link } from "react-router-dom";
import { UserAvatar } from "@/components/UserAvatar";
import { CalendarDays, FolderKanban, Users, Cake, Trophy, RefreshCw, User, Building2 } from "lucide-react";

export default function Dashboard() {
    const [state, setState] = useState({ projects: [], clients: [], users: [], tasks: [], statuses: [] });
    const [success, setSuccess] = useState(null);
    const [conditionProject, setConditionProject] = useState(null);
    const [conditionPrograms, setConditionPrograms] = useState([]);
    const [rotating, setRotating] = useState(false);

    const loadRandomSuccess = async () => {
        setRotating(true);
        try {
            const { data } = await api.get("/successes/random");
            setSuccess(data);
        } catch { setSuccess(null); }
        finally { setRotating(false); }
    };

    useEffect(() => {
        (async () => {
            const [p, c, u, s] = await Promise.all([
                api.get("/projects"),
                api.get("/clients"),
                api.get("/users"),
                api.get("/settings/project-statuses"),
            ]);
            // Fetch tasks from all projects (spot + continuous now share structure)
            const activeProjects = p.data.filter((x) => !x.is_deleted);
            const tasksAll = [];
            for (const proj of activeProjects) {
                try {
                    const t = await api.get(`/projects/${proj.id}/tasks`);
                    t.data.forEach((tt) => tasksAll.push({ ...tt, project_title: proj.title, project_id: proj.id }));
                } catch (e) { console.error(`Load tasks for ${proj.id} failed:`, e); }
            }
            setState({ projects: p.data, clients: c.data, users: u.data, tasks: tasksAll, statuses: s.data });
            loadRandomSuccess();

            // Load current "Piano settimana" (most recent internal_weekly)
            const weeklies = activeProjects.filter((x) => x.project_type === "internal_weekly")
                .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
            if (weeklies.length > 0) {
                setConditionProject(weeklies[0]);
                try {
                    const pr = await api.get(`/projects/${weeklies[0].id}/programs`);
                    setConditionPrograms(pr.data);
                } catch (e) { console.error("Load condition programs failed:", e); }
            }
        })();
    }, []);

    const today = new Date();
    const in30 = new Date(today.getTime() + 30 * 86400000);

    const upcomingBirthdays = state.clients
        .map((c) => {
            if (!c.birthday) return null;
            const b = new Date(c.birthday);
            const next = new Date(today.getFullYear(), b.getMonth(), b.getDate());
            if (next < today) next.setFullYear(today.getFullYear() + 1);
            return { c, next };
        })
        .filter(Boolean)
        .filter((x) => x.next <= in30)
        .sort((a, b) => a.next - b.next);

    const upcomingDeadlines = state.projects
        .filter((p) => p.end_date && new Date(p.end_date) >= today && new Date(p.end_date) <= in30)
        .sort((a, b) => new Date(a.end_date) - new Date(b.end_date));

    const inCorsoIds = new Set(state.statuses.filter((s) => (s.name || "").toLowerCase() === "in corso").map((s) => s.id));
    const activeProjects = state.projects
        .filter((p) => !p.is_deleted)
        .filter((p) => p.project_type !== "internal_long" && p.project_type !== "internal_weekly")
        .filter((p) => inCorsoIds.has(p.status_id))
        .slice(0, 6);

    // For each active project, find the next task to work on:
    // - secondary tasks ordered by `order`
    // - if a secondary has non-completed tertiary children, pick the first (by order) tertiary
    // - otherwise, if the secondary itself is not completed, pick it
    const firstPendingByProject = {};
    activeProjects.forEach((p) => {
        const tks = state.tasks.filter((t) => t.project_id === p.id && !t.is_deleted);
        const secondaries = tks.filter((t) => t.level === "secondary").sort((a, b) => (a.order || 0) - (b.order || 0));
        for (const s of secondaries) {
            const children = tks.filter((t) => t.parent_task_id === s.id).sort((a, b) => (a.order || 0) - (b.order || 0));
            if (children.length > 0) {
                const nextChild = children.find((c) => !c.completed);
                if (nextChild) { firstPendingByProject[p.id] = nextChild; break; }
            } else if (!s.completed) {
                firstPendingByProject[p.id] = s; break;
            }
        }
    });

    return (
        <div className="p-6" data-testid="dashboard-page">
            <div className="flex items-baseline justify-between mb-6">
                <div>
                    <h1 className="font-display text-2xl font-semibold tracking-tight text-neutral-900">Dashboard</h1>
                    <div className="text-sm text-neutral-500 mt-0.5">Panoramica operativa</div>
                </div>
                <div className="text-xs text-neutral-500">Oggi: <b className="text-neutral-800">{formatDateIt(today.toISOString())}</b></div>
            </div>

            {success && (
                <div className="mb-4 rounded-md bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 p-5 flex gap-4 items-start" data-testid="success-card">
                    <div className="p-3 rounded-md bg-white border border-amber-200 text-amber-600 shrink-0"><Trophy size={22} /></div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-widest font-semibold text-amber-700">Successo dello studio</div>
                        {success.title && <div className="font-display font-semibold text-neutral-900 mt-0.5">{success.title}</div>}
                        <div className="text-sm text-neutral-800 whitespace-pre-line mt-1 line-clamp-3">{success.content}</div>
                        <div className="flex flex-wrap gap-1.5 mt-2 text-xs">
                            {success.is_studio && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-amber-200 bg-white text-amber-800"><Building2 size={10} /> Studio</span>}
                            {(success.clients || []).map((c) => (
                                <Link key={c.id} to={`/clienti/${c.id}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-sky-200 bg-white text-sky-800 hover:bg-sky-50"><User size={10} /> {c.first_name} {c.last_name}</Link>
                            ))}
                            {(success.collaborators || []).map((c) => (
                                <Link key={c.id} to={`/collaboratori/${c.id}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-emerald-200 bg-white text-emerald-800 hover:bg-emerald-50">{c.name}</Link>
                            ))}
                        </div>
                    </div>
                    <button onClick={loadRandomSuccess} data-testid="success-shuffle"
                        title="Mostra un altro successo"
                        className="text-neutral-500 hover:text-neutral-900">
                        <RefreshCw size={14} className={rotating ? "animate-spin" : ""} />
                    </button>
                </div>
            )}

            {conditionProject && (
                <div className="mb-6 bg-white border border-neutral-200 rounded-md p-5" data-testid="condition-card">
                    <div className="flex items-baseline justify-between mb-2">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500">Piano settimanale</div>
                            <Link to={`/progetti/${conditionProject.id}`} className="font-display text-lg font-semibold text-neutral-900 hover:underline">
                                {conditionProject.title}
                            </Link>
                        </div>
                    </div>
                    {conditionPrograms.length === 0 ? (
                        <div className="text-sm text-neutral-400">Nessun programma per la settimana corrente.</div>
                    ) : (
                        <ul className="divide-y divide-neutral-100">
                            {conditionPrograms.map((pr) => (
                                <li key={pr.id} className="py-2 text-sm text-neutral-800 flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-neutral-400" />
                                    <span className="font-medium">{pr.title}</span>
                                    {pr.description && <span className="text-xs text-neutral-500 truncate">— {pr.description}</span>}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <StatCard label="Clienti" value={state.clients.length} icon={<Users size={16} />} testId="stat-clients" />
                <StatCard label="Progetti attivi" value={state.projects.length} icon={<FolderKanban size={16} />} testId="stat-projects" />
                <StatCard label="Scadenze 30gg" value={upcomingDeadlines.length} icon={<CalendarDays size={16} />} testId="stat-deadlines" />
                <StatCard label="Compleanni 30gg" value={upcomingBirthdays.length} icon={<Cake size={16} />} testId="stat-birthdays" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Panel title="Progetti attivi" testId="panel-projects">
                    {activeProjects.length === 0 && <EmptyState text="Nessun progetto in corso." />}
                    <ul className="divide-y divide-neutral-100">
                        {activeProjects.map((p) => {
                            const t = firstPendingByProject[p.id];
                            return (
                                <li key={p.id} className="py-2.5">
                                    <Link to={`/progetti/${p.id}`} className="block hover:bg-neutral-50 -mx-2 px-2 py-1 rounded">
                                        <div className="text-sm text-neutral-900 truncate" data-testid={`dashboard-next-task-${p.id}`}>
                                            <span className="font-medium">{p.title}</span>
                                            {t && <span className="text-neutral-400"> → </span>}
                                            {t && <span className="text-neutral-700">{t.title}</span>}
                                        </div>
                                        <div className="text-xs text-neutral-500 flex justify-between">
                                            <span>{p.project_type === "continuous" ? "Continuativo" : "Spot"}</span>
                                            <span>{formatDateIt(p.end_date)}</span>
                                        </div>
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </Panel>

                <Panel title="Scadenze imminenti" testId="panel-deadlines">
                    {upcomingDeadlines.length === 0 && <EmptyState text="Nessuna scadenza nei prossimi 30 giorni." />}
                    <ul className="divide-y divide-neutral-100">
                        {upcomingDeadlines.map((p) => (
                            <li key={p.id} className="py-2.5 flex items-center justify-between">
                                <Link to={`/progetti/${p.id}`} className="text-sm font-medium text-neutral-900 hover:underline truncate">{p.title}</Link>
                                <span className="text-xs text-neutral-600 font-medium">{formatDateIt(p.end_date)}</span>
                            </li>
                        ))}
                    </ul>
                </Panel>

                <Panel title="Compleanni clienti" testId="panel-birthdays">
                    {upcomingBirthdays.length === 0 && <EmptyState text="Nessun compleanno nei prossimi 30 giorni." />}
                    <ul className="divide-y divide-neutral-100">
                        {upcomingBirthdays.map(({ c, next }) => (
                            <li key={c.id} className="py-2.5 flex items-center justify-between">
                                <Link to={`/clienti/${c.id}`} className="text-sm font-medium text-neutral-900 hover:underline truncate">
                                    {c.first_name} {c.last_name}
                                </Link>
                                <span className="text-xs text-neutral-600">{formatDateIt(next.toISOString())}</span>
                            </li>
                        ))}
                    </ul>
                </Panel>
            </div>
        </div>
    );
}

function StatCard({ label, value, icon, testId }) {
    return (
        <div data-testid={testId} className="bg-white border border-neutral-200 rounded-md p-4">
            <div className="flex items-center justify-between text-neutral-500">
                <span className="text-xs uppercase tracking-wider font-medium">{label}</span>
                {icon}
            </div>
            <div className="mt-2 font-display text-3xl font-semibold tracking-tight text-neutral-900">{value}</div>
        </div>
    );
}

function Panel({ title, children, testId }) {
    return (
        <div className="bg-white border border-neutral-200 rounded-md" data-testid={testId}>
            <div className="px-4 py-3 border-b border-neutral-100 font-display text-sm font-semibold text-neutral-800">{title}</div>
            <div className="px-4 py-2">{children}</div>
        </div>
    );
}

function EmptyState({ text }) {
    return <div className="text-sm text-neutral-400 py-3">{text}</div>;
}
